import React, { useEffect, useMemo, useState } from 'react';
import { createUser, deleteUser, getUsers, updateUser } from '../../services/userService';
import './Users.css';

const DEFAULT_EXPIRES_AT = '2055-01-01 15:04:05';

const Users = () => {
  const [activeTab, setActiveTab] = useState('admin');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    address: '',
    phone: '',
    active: 1,
  });

  const adminQueryDefaults = useMemo(() => {
    const projectId = (localStorage.getItem('user_project_id') || '').trim();
    const clientPlatformId = (localStorage.getItem('user_client_platform_id') || '').trim();
    const clientTypeId = (localStorage.getItem('user_client_type_id') || '').trim();

    const platformIdFromEnv = (process.env.REACT_APP_PLATFORM_ID || '').trim();
    const resolvedClientPlatformId = clientPlatformId || platformIdFromEnv;

    return {
      projectId,
      clientPlatformId: resolvedClientPlatformId,
      clientTypeId,
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'admin') return;
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, limit, search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const params = {
        limit,
        offset,
        search,
      };

      if (adminQueryDefaults.clientPlatformId) {
        params['client-platform-id'] = adminQueryDefaults.clientPlatformId;
      }
      if (adminQueryDefaults.clientTypeId) {
        params['client-type-id'] = adminQueryDefaults.clientTypeId;
      }
      if (adminQueryDefaults.projectId) {
        params['project-id'] = adminQueryDefaults.projectId;
      }

      const response = await getUsers(params);
      setUsers(response.data.users || []);
      setTotal(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openCreateModal = () => {
    setCurrentUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      address: '',
      phone: '',
      active: 1,
    });
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setCurrentUser(user);
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      password: '',
      address: user?.info?.address || '',
      phone: user?.phone || '',
      active: typeof user?.active === 'number' ? user.active : 1,
    });
    setModalOpen(true);
  };

  const buildCreatePayload = () => {
    const roleId = (localStorage.getItem('user_role_id') || '').trim();
    const projectId = (localStorage.getItem('user_project_id') || '').trim();
    const clientPlatformId = adminQueryDefaults.clientPlatformId;
    const clientTypeId = adminQueryDefaults.clientTypeId;

    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      active: Number(formData.active),
      info: {
        address: formData.address,
      },
      expires_at: DEFAULT_EXPIRES_AT,
    };

    if (projectId) payload.project_id = projectId;
    if (clientPlatformId) payload.client_platform_id = clientPlatformId;
    if (clientTypeId) payload.client_type_id = clientTypeId;
    if (roleId) payload.role_id = roleId;

    return payload;
  };

  const buildUpdatePayload = () => {
    const payload = {
      id: currentUser?.id,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      active: Number(formData.active),
      info: {
        ...(currentUser?.info || {}),
        address: formData.address,
      },
      project_id: currentUser?.project_id,
      client_platform_id: currentUser?.client_platform_id,
      client_type_id: currentUser?.client_type_id,
      role_id: currentUser?.role_id,
      expires_at: currentUser?.expires_at,
      photo_url: currentUser?.photo_url,
      login: currentUser?.login,
    };

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);

      if (currentUser) {
        await updateUser(buildUpdatePayload());
      } else {
        await createUser(buildCreatePayload());
      }

      setModalOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(`Delete user "${user?.name || user?.email || ''}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteUser(user.id);
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="users-page">
      <div className="users-tabs" role="tablist" aria-label="Users tabs">
        <button
          type="button"
          className={`users-tab ${activeTab === 'admin' ? 'active' : ''}`}
          onClick={() => setActiveTab('admin')}
          role="tab"
          aria-selected={activeTab === 'admin'}
        >
          Admin Users
        </button>
        <button
          type="button"
          className={`users-tab ${activeTab === 'telegram' ? 'active' : ''}`}
          onClick={() => setActiveTab('telegram')}
          role="tab"
          aria-selected={activeTab === 'telegram'}
        >
          Telegram Users
        </button>
      </div>

      {activeTab === 'telegram' ? (
        <div className="users-placeholder">
          Telegram Users tab is not implemented yet.
        </div>
      ) : (
        <>
          <div className="page-header">
            <div className="search-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={handleSearch}
                aria-label="Search admin users"
              />
            </div>
            <div className="header-actions">
              <button className="add-btn" onClick={openCreateModal} type="button">
                + Add User
              </button>
            </div>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Active</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ color: 'var(--text-secondary)' }}>
                      Loading...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ color: 'var(--text-secondary)' }}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr key={user.id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>
                        <span
                          className={`status-pill ${user.active === 1 ? 'active' : 'inactive'}`}
                          aria-label={user.active === 1 ? 'Active' : 'Inactive'}
                        >
                          {user.active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{user.created_at}</td>
                      <td>
                        <button
                          className="action-btn"
                          onClick={() => openEditModal(user)}
                          type="button"
                          aria-label="Edit user"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDelete(user)}
                          type="button"
                          aria-label="Delete user"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <div className="pagination-info">
              Showing {total === 0 ? 0 : (page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
            </div>
            <div className="pagination-controls">
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className="page-btn"
                type="button"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                  onClick={() => handlePageChange(i + 1)}
                  type="button"
                >
                  {i + 1}
                </button>
              ))}
              <button
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="page-btn"
                type="button"
              >
                Next
              </button>
            </div>
          </div>

          {modalOpen && (
            <div className="modal-overlay" role="dialog" aria-modal="true">
              <div className="modal">
                <div className="modal-header">
                  <h2>{currentUser ? 'Edit User' : 'Create User'}</h2>
                  <button className="close-btn" onClick={() => setModalOpen(false)} type="button" aria-label="Close modal">
                    &times;
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label htmlFor="user-name">Name</label>
                      <input
                        id="user-name"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="user-email">Email</label>
                      <input
                        id="user-email"
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                        required
                      />
                    </div>

                    {!currentUser && (
                      <div className="form-group">
                        <label htmlFor="user-password">Password</label>
                        <input
                          id="user-password"
                          type="password"
                          className="form-control"
                          value={formData.password}
                          onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                          required
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="user-phone">Phone</label>
                      <input
                        id="user-phone"
                        className="form-control"
                        value={formData.phone}
                        onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="user-address">Address</label>
                      <input
                        id="user-address"
                        className="form-control"
                        value={formData.address}
                        onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="user-active">Active</label>
                      <select
                        id="user-active"
                        className="form-control"
                        value={String(formData.active)}
                        onChange={(e) => setFormData((p) => ({ ...p, active: Number(e.target.value) }))}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-save" disabled={saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Users;
